import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import {
  getTemporaryRolesAndPermissionsDropdown,
  getNutritionistDetailsWithRole,
  getPermissionsListWithRole,
  assignRoleToNutritionist,
} from "../../services/team";
import { useAuth } from "../../context/auth/AuthContext";
import { Button } from "../Ui_Components/Button";

const DURATION_TO_EXPIRES: Record<"1day" | "1week" | "1month", string> = {
  "1day": "1_day",
  "1week": "1_week",
  "1month": "1_month",
};

const setsEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false;
  let equal = true;
  a.forEach((v) => {
    if (!b.has(v)) equal = false;
  });
  return equal;
};

export interface TeamMember {
  id: number;
  nutritionId: number;
  name: string;
  designation: string;
  reportingManager: string;
  clientsAssigned: number;
  joiningDate: string;
  status: "Active" | "Inactive";
  initials: string;
  avatarColor: string;
  employeeId: string;
  currentPermissions: string[];
  profileUrl?: string | null;
}

interface RoleOption {
  value: string;
  label: string;
}

interface PermissionActionOption {
  value: string;
  label: string;
  selected?: boolean;
  locked?: boolean;
}

interface PermissionResourceGroup {
  resource: string;
  resource_label: string;
  actions: PermissionActionOption[];
}

// Permissions repeat their `value` across resources (e.g. "view" exists for
// meal_plan, exercise_plan, etc.) so we key selections by `resource:value`.
const permKey = (resource: string, value: string) => `${resource}:${value}`;

// ─── Checkmark SVG ────────────────────────────────────────────────────────────
const CheckIcon: React.FC = () => (
  <svg
    className="w-2.5 h-2.5 text-white"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={3}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

// ─── Reusable Checkbox ────────────────────────────────────────────────────────
const Checkbox: React.FC<{
  checked: boolean;
  onChange: () => void;
  label?: string;
}> = ({ checked, onChange, label }) => (
  <label
    className="flex items-center gap-2 cursor-pointer select-none"
    onClick={onChange}
  >
    <div
      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
        checked ? "bg-[#00A9A9] border-[#00A9A9]" : "border-gray-300 bg-white"
      }`}
    >
      {checked && <CheckIcon />}
    </div>
    {label && (
      <span className="text-sm 3xl:text-[16px] font-inter text-[#1F1F1F]">{label}</span>
    )}
  </label>
);

// ─── Permission chips with overflow tooltip ──────────────────────────────────
const PERMISSION_CHIP_LIMIT = 3;

const PermissionChips: React.FC<{ labels: string[] }> = ({ labels }) => {
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const moreRef = useRef<HTMLSpanElement>(null);

  if (labels.length === 0) return null;

  const visible = labels.slice(0, PERMISSION_CHIP_LIMIT);
  const remaining = labels.length - visible.length;

  const openTooltip = () => {
    if (!moreRef.current) return;
    const r = moreRef.current.getBoundingClientRect();
    setAnchor({ top: r.bottom + 6, left: r.left });
  };

  return (
    <>
      {visible.map((perm) => (
        <span
          key={perm}
          className="px-2.5 py-0.5 bg-[#F0F0F0] text-[#1F1F1F] text-[10px] 3xl:text-[12px] font-inter rounded-full whitespace-nowrap"
        >
          {perm}
        </span>
      ))}
      {remaining > 0 && (
        <span
          ref={moreRef}
          onMouseEnter={openTooltip}
          onMouseLeave={() => setAnchor(null)}
          className="px-2.5 py-0.5 bg-[#F0F0F0] text-[#0DB5B5] text-[10px] 3xl:text-[12px] font-inter font-semibold rounded-full cursor-default"
        >
          +{remaining}
        </span>
      )}
      {anchor &&
        remaining > 0 &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: anchor.top,
              left: anchor.left,
              zIndex: 9999,
              border: "0.5px solid #F3F4F6",
              pointerEvents: "none",
            }}
            className="bg-white rounded-lg px-2 py-2 shadow-md inline-flex max-w-[260px]"
          >
            <div className="flex flex-wrap gap-1.5">
              {labels.map((perm) => (
                <span
                  key={perm}
                  className="px-2.5 py-0.5 bg-[#F0F0F0] text-[#1F1F1F] text-[10px] 3xl:text-[12px] font-inter rounded-full whitespace-nowrap"
                >
                  {perm}
                </span>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// ─── Confirmation modal ───────────────────────────────────────────────────────
const ConfirmModal: React.FC<{
  memberName: string;
  role: string;
  roleChanged: boolean;
  addedLabels: string[];
  revokedLabels: string[];
  temporaryAccess: boolean;
  expiryDateText: string;
  submitting: boolean;
  submitError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ memberName, role, roleChanged, addedLabels, revokedLabels, temporaryAccess, expiryDateText, submitting, submitError, onConfirm, onCancel }) => {
  const addedText = addedLabels.join(", ");
  const revokedText = revokedLabels.join(", ");
  const hasAdded = addedLabels.length > 0;
  const hasRevoked = revokedLabels.length > 0;

  // Suffix only attaches to "granted" — revoked actions are immediate.
  const grantedSuffix =
    temporaryAccess && hasAdded ? (
      <> until <span className="font-semibold">{expiryDateText}</span></>
    ) : null;

  const renderMessage = () => {
    if (roleChanged && hasAdded && hasRevoked) {
      return (
        <>
          <span className="font-semibold">"{memberName}"</span> role has been changed to{" "}
          <span className="font-semibold">"{role}"</span>,{" "}
          <span className="font-semibold">"{addedText}"</span> permissions have been granted
          {grantedSuffix} and{" "}
          <span className="font-semibold">"{revokedText}"</span> permissions have been revoked.
        </>
      );
    }
    if (roleChanged && hasAdded) {
      return (
        <>
          <span className="font-semibold">"{memberName}"</span> role has been changed to{" "}
          <span className="font-semibold">"{role}"</span> and{" "}
          <span className="font-semibold">"{addedText}"</span> permissions have been granted
          {grantedSuffix}.
        </>
      );
    }
    if (roleChanged && hasRevoked) {
      return (
        <>
          <span className="font-semibold">"{memberName}"</span> role has been changed to{" "}
          <span className="font-semibold">"{role}"</span> and{" "}
          <span className="font-semibold">"{revokedText}"</span> permissions have been revoked.
        </>
      );
    }
    if (roleChanged) {
      return (
        <>
          <span className="font-semibold">"{memberName}"</span> role has been changed to{" "}
          <span className="font-semibold">"{role}"</span>.
        </>
      );
    }
    if (hasAdded && hasRevoked) {
      return (
        <>
          <span className="font-semibold">"{addedText}"</span> permissions have been granted
          {grantedSuffix} and{" "}
          <span className="font-semibold">"{revokedText}"</span> permissions have been revoked for{" "}
          <span className="font-semibold">"{memberName}"</span>.
        </>
      );
    }
    if (hasAdded) {
      return (
        <>
          <span className="font-semibold">"{addedText}"</span> permissions have been granted to{" "}
          <span className="font-semibold">"{memberName}"</span>
          {grantedSuffix}.
        </>
      );
    }
    if (hasRevoked) {
      return (
        <>
          <span className="font-semibold">"{revokedText}"</span> permissions have been revoked from{" "}
          <span className="font-semibold">"{memberName}"</span>.
        </>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
        <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
          Are you sure?
        </h2>
        <div className="border-t border-gray-100 my-4" />
        <p className="text-[#1F1F1F] text-sm 3xl:text-[16px] font-inter leading-relaxed">
          {renderMessage()}
        </p>
        {submitError && (
          <p className="mt-3 text-sm font-inter text-red-500">{submitError}</p>
        )}
        <div className="border-t border-gray-100 my-5" />
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Yes, update"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Change Role Modal ────────────────────────────────────────────────────────
const ChangeRoleModal: React.FC<{
  member: TeamMember;
  onClose: () => void;
  onSaved: () => void;
  onError?: (message: string) => void;
}> = ({ member, onClose, onSaved, onError }) => {
  const { user } = useAuth();

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionResourceGroup[]>([]);
  const [lockedPerms, setLockedPerms] = useState<Set<string>>(new Set());
  const [currentPermissionLabels, setCurrentPermissionLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [temporaryAccess, setTemporaryAccess] = useState(false);
  const [duration, setDuration] = useState<"1day" | "1week" | "1month">("1day");
  const [roleOpen, setRoleOpen] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Frozen at load — used to diff against current state when building the
  // ASSIGN_ROLE_TO_NUTRITIONIST payload so we only send what actually changed.
  const initialRoleRef = useRef<string>("");
  const initialDirectGrantsRef = useRef<Set<string>>(new Set());

  // Guards against race conditions when the user rapidly switches roles.
  const previewReqIdRef = useRef(0);

  const roleRef = useRef<HTMLDivElement>(null);
  const permsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (roleRef.current && !roleRef.current.contains(e.target as Node))
        setRoleOpen(false);
      if (permsRef.current && !permsRef.current.contains(e.target as Node))
        setPermsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [dropdownData, detailsData] = await Promise.all([
          getTemporaryRolesAndPermissionsDropdown(),
          getNutritionistDetailsWithRole(member.nutritionId),
        ]);

        if (cancelled) return;

        if (dropdownData.success !== "1")
          throw new Error(dropdownData.message || "Failed to load dropdowns");
        if (detailsData.success !== "1")
          throw new Error(detailsData.message || "Failed to load nutritionist");

        setRoleOptions(dropdownData.roles ?? []);
        setPermissionGroups(dropdownData.permissions ?? []);

        const initialRole = detailsData.nutritionist?.role ?? "";
        setSelectedRole(initialRole);
        initialRoleRef.current = initialRole;

        const preSelected = new Set<string>();
        const locked = new Set<string>();
        const initialLabels: string[] = [];
        const permState: PermissionResourceGroup[] =
          detailsData.permission_state ?? [];
        permState.forEach((group) => {
          group.actions.forEach((action) => {
            const key = permKey(group.resource, action.value);
            if (action.selected) {
              preSelected.add(key);
              initialLabels.push(`${group.resource_label} · ${action.label}`);
            }
            if (action.locked) locked.add(key);
          });
        });
        setSelectedPerms(preSelected);
        setLockedPerms(locked);
        setCurrentPermissionLabels(initialLabels);

        // Direct grants = selected and NOT locked (locked items are role
        // defaults — not part of the `permissions` payload we send back).
        const initialDirectGrants = new Set<string>();
        preSelected.forEach((k) => {
          if (!locked.has(k)) initialDirectGrants.add(k);
        });
        initialDirectGrantsRef.current = initialDirectGrants;
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Failed to load role data",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [member.nutritionId]);

  // When the user picks a new role, fetch the preview of permissions for that
  // role and rebuild the selected/locked sets. We keep the master
  // `permissionGroups` (from the dropdown API) as the structural source of
  // truth — only the per-action state changes here.
  const handleRoleSelect = async (newRole: string) => {
    setRoleOpen(false);
    if (!newRole || newRole === selectedRole) return;
    setSelectedRole(newRole);

    const reqId = ++previewReqIdRef.current;
    setPreviewLoading(true);
    setLoadError(null);
    try {
      const data = await getPermissionsListWithRole(member.nutritionId, newRole);
      if (reqId !== previewReqIdRef.current) return;
      if (data.success !== "1")
        throw new Error(data.message || "Failed to load permissions for role");

      const nextSelected = new Set<string>();
      const nextLocked = new Set<string>();
      const permState: PermissionResourceGroup[] = data.permission_state ?? [];
      permState.forEach((group) => {
        group.actions.forEach((action) => {
          const key = permKey(group.resource, action.value);
          if (action.selected) nextSelected.add(key);
          if (action.locked) nextLocked.add(key);
        });
      });
      setSelectedPerms(nextSelected);
      setLockedPerms(nextLocked);
    } catch (err) {
      if (reqId !== previewReqIdRef.current) return;
      setLoadError(
        err instanceof Error ? err.message : "Failed to load permissions for role",
      );
    } finally {
      if (reqId === previewReqIdRef.current) setPreviewLoading(false);
    }
  };

  const togglePerm = (resource: string, value: string) => {
    const key = permKey(resource, value);
    if (lockedPerms.has(key)) return;
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedLabels = useMemo(() => {
    const labels: string[] = [];
    permissionGroups.forEach((group) => {
      group.actions.forEach((action) => {
        if (selectedPerms.has(permKey(group.resource, action.value))) {
          labels.push(`${group.resource_label} · ${action.label}`);
        }
      });
    });
    return labels;
  }, [permissionGroups, selectedPerms]);

  // Labels for permissions added in this session — used in the confirmation
  // modal so the user sees only what's NEW, not the existing role defaults.
  const newlyAddedLabels = useMemo(() => {
    const labels: string[] = [];
    permissionGroups.forEach((group) => {
      group.actions.forEach((action) => {
        const key = permKey(group.resource, action.value);
        if (
          selectedPerms.has(key) &&
          !lockedPerms.has(key) &&
          !initialDirectGrantsRef.current.has(key)
        ) {
          labels.push(`${group.resource_label} · ${action.label}`);
        }
      });
    });
    return labels;
  }, [permissionGroups, selectedPerms, lockedPerms]);

  // Labels for permissions removed in this session.
  const revokedLabels = useMemo(() => {
    const labels: string[] = [];
    permissionGroups.forEach((group) => {
      group.actions.forEach((action) => {
        const key = permKey(group.resource, action.value);
        if (
          initialDirectGrantsRef.current.has(key) &&
          !selectedPerms.has(key)
        ) {
          labels.push(`${group.resource_label} · ${action.label}`);
        }
      });
    });
    return labels;
  }, [permissionGroups, selectedPerms]);

  const permsDisplay =
    selectedPerms.size === 0
      ? "Select permissions"
      : selectedPerms.size === 1
      ? selectedLabels[0] ?? "1 permission"
      : `${selectedPerms.size} permissions`;

  const canSave = selectedRole !== "" && selectedPerms.size > 0;

  const handleSave = async () => {
    if (!user?.nutrist_id) {
      setSubmitError("Missing caller identity. Please sign in again.");
      return;
    }

    // Direct grants in the current selection (excluding role-default locked ones).
    const currentDirectGrants = new Set<string>();
    selectedPerms.forEach((k) => {
      if (!lockedPerms.has(k)) currentDirectGrants.add(k);
    });

    const roleChanged = selectedRole !== initialRoleRef.current;
    const permsChanged = !setsEqual(currentDirectGrants, initialDirectGrantsRef.current);

    // Nothing to send → treat as a no-op success.
    if (!roleChanged && !permsChanged) {
      onSaved();
      onClose();
      return;
    }

    const payload: Record<string, unknown> = {
      target_nutrist_id: member.nutritionId,
      caller_id: user.nutrist_id,
    };
    if (roleChanged) payload.role = selectedRole;
    if (permsChanged) {
      payload.permissions = Array.from(currentDirectGrants).map((k) => {
        const [resource, action] = k.split(":");
        return { resource, action };
      });
      if (temporaryAccess) {
        payload.temporary = true;
        payload.expires_in = DURATION_TO_EXPIRES[duration];
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await assignRoleToNutritionist(payload);
      if (data.success !== "1") {
        throw new Error(data.message || `Update failed`);
      }
      onSaved();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update role & permissions";
      setSubmitError(message);
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getTomorrowLabel = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const h = d.getHours() % 12 || 12;
    const m = d.getMinutes().toString().padStart(2, "0");
    const ampm = d.getHours() >= 12 ? "pm" : "am";
    return `Until tomorrow, ${h}:${m} ${ampm}`;
  };

  // "31 July"-style date for the temporary-access expiry, based on the
  // currently picked duration.
  const getExpiryDateText = () => {
    const d = new Date();
    if (duration === "1day") d.setDate(d.getDate() + 1);
    else if (duration === "1week") d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 30);
    const month = d.toLocaleString("en-US", { month: "long" });
    return `${d.getDate()} ${month}`;
  };

  const durationOptions = [
    { value: "1day",   label: "1 day",   desc: getTomorrowLabel() },
    { value: "1week",  label: "1 week",  desc: "7 days from now" },
    { value: "1month", label: "1 month", desc: "30 days from now" },
  ] as const;

  const selectedRoleLabel =
    roleOptions.find((r) => r.value === selectedRole)?.label || selectedRole;

  if (showConfirm) {
    return (
      <ConfirmModal
        memberName={member.name}
        role={selectedRoleLabel}
        roleChanged={selectedRole !== initialRoleRef.current}
        addedLabels={newlyAddedLabels}
        revokedLabels={revokedLabels}
        temporaryAccess={temporaryAccess}
        expiryDateText={getExpiryDateText()}
        submitting={submitting}
        submitError={submitError}
        onConfirm={handleSave}
        onCancel={() => {
          if (submitting) return;
          setSubmitError(null);
          setShowConfirm(false);
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
            Change role & permissions
          </h2>
        </div>
        <div className="border-t border-[#E8E8E8] mx-6" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Member card */}
          <div className="border border-[#E8E8E8] rounded-lg p-3">
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm 3xl:text-[16px] font-semibold font-inter flex-shrink-0"
                style={{ backgroundColor: member.avatarColor }}
              >
                {member.initials}
              </div>
              <div>
                <p className="font-semibold font-inter text-[#1F1F1F] text-sm 3xl:text-[16px]">
                  {member.name}
                </p>
                <p className="text-xs 3xl:text-[14px] font-inter text-gray-500">
                  {member.designation}
                  {member.employeeId ? `, ${member.employeeId}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs 3xl:text-[14px] font-inter text-[#646464]">
                Current permissions :
              </span>
              {loading ? (
                <span className="text-xs 3xl:text-[14px] font-inter text-gray-400">
                  Loading...
                </span>
              ) : currentPermissionLabels.length === 0 ? (
                <span className="text-xs 3xl:text-[14px] font-inter text-gray-400">
                  None
                </span>
              ) : (
                <PermissionChips labels={currentPermissionLabels} />
              )}
            </div>
          </div>

          {loadError && (
            <p className="text-sm font-inter text-red-500">{loadError}</p>
          )}

          {/* Change to */}
          <div>
            <p className="text-sm 3xl:text-[16px] font-inter text-[#646464] mb-3">Change to :</p>
            <div className="grid grid-cols-2 gap-4">
              {/* Role dropdown */}
              <div>
                <label className="block text-sm 3xl:text-[16px] font-semibold font-inter text-[#1F1F1F] mb-2">
                  Role
                </label>
                <div className="relative" ref={roleRef}>
                  <button
                    type="button"
                    disabled={loading || previewLoading}
                    onClick={() => {
                      setRoleOpen((o) => !o);
                      setPermsOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg text-sm 3xl:text-[16px] font-inter bg-white hover:border-gray-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={selectedRole ? "text-[#1F1F1F]" : "text-gray-400"}>
                      {loading
                        ? "Loading..."
                        : selectedRoleLabel || "Select role"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                  {roleOpen && !loading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto custom-scrollbar-overlay scrollbar-visible">
                      {roleOptions.length === 0 ? (
                        <p className="px-4 py-2 text-sm font-inter text-gray-400">
                          No roles available
                        </p>
                      ) : (
                        roleOptions.map((role) => (
                          <button
                            key={role.value}
                            type="button"
                            onClick={() => handleRoleSelect(role.value)}
                            className={`w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter hover:bg-gray-50 transition-colors ${
                              selectedRole === role.value
                                ? "text-[#00A9A9] font-medium"
                                : "text-[#1F1F1F]"
                            }`}
                          >
                            {role.label}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions dropdown */}
              <div>
                <label className="block text-sm 3xl:text-[16px] font-semibold font-inter text-[#1F1F1F] mb-2">
                  Permissions
                </label>
                <div className="relative" ref={permsRef}>
                  <button
                    type="button"
                    disabled={loading || previewLoading}
                    onClick={() => {
                      setPermsOpen((o) => !o);
                      setRoleOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg text-sm 3xl:text-[16px] font-inter bg-white hover:border-gray-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span
                      className={
                        selectedPerms.size > 0 ? "text-[#1F1F1F]" : "text-gray-400"
                      }
                    >
                      {loading || previewLoading ? "Loading..." : permsDisplay}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                  {permsOpen && !loading && !previewLoading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-2 max-h-72 overflow-y-auto custom-scrollbar-overlay scrollbar-visible">
                      {permissionGroups.length === 0 ? (
                        <p className="px-4 py-2 text-sm font-inter text-gray-400">
                          No permissions available
                        </p>
                      ) : (
                        permissionGroups.map((group) => (
                          <div key={group.resource} className="py-1">
                            <p className="px-4 py-1 text-[11px] 3xl:text-[12px] font-semibold font-inter text-[#646464] uppercase tracking-wide">
                              {group.resource_label}
                            </p>
                            {group.actions.map((action) => {
                              const key = permKey(group.resource, action.value);
                              const checked = selectedPerms.has(key);
                              const isLocked = lockedPerms.has(key);
                              return (
                                <div
                                  key={key}
                                  className={`flex items-center gap-3 px-4 py-2 ${
                                    isLocked
                                      ? "opacity-60 cursor-not-allowed"
                                      : "hover:bg-gray-50 cursor-pointer"
                                  }`}
                                  onClick={() =>
                                    togglePerm(group.resource, action.value)
                                  }
                                >
                                  <div
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                                      checked
                                        ? "bg-[#00A9A9] border-[#00A9A9]"
                                        : "border-gray-300 bg-white"
                                    }`}
                                  >
                                    {checked && <CheckIcon />}
                                  </div>
                                  <span className="text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] flex-1">
                                    {action.label}
                                  </span>
                                  {isLocked && (
                                    <span className="text-[10px] 3xl:text-[12px] font-inter text-[#646464]">
                                      Locked
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Temporary access */}
          <div>
            <Checkbox
              checked={temporaryAccess}
              onChange={() => setTemporaryAccess((t) => !t)}
              label="Temporary access"
            />

            {temporaryAccess && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                {durationOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDuration(opt.value)}
                    className={`flex items-start gap-2 p-3 rounded-lg border-[0.5px] text-left transition-colors ${
                      duration === opt.value
                        ? "border-[#20BCBB] bg-[#F1FBFB]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        duration === opt.value
                          ? "border-[#20BCBB]"
                          : "border-gray-300"
                      }`}
                    >
                      {duration === opt.value && (
                        <div className="w-2 h-2 rounded-full bg-[#20BCBB]" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm 3xl:text-[16px] font-medium font-inter text-[#1F1F1F]">
                        {opt.label}
                      </p>
                      <p className="text-xs 3xl:text-[14px] font-inter text-gray-400 mt-0.5">
                        {opt.desc}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[#E8E8E8] mx-6" />

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => canSave && setShowConfirm(true)}
            disabled={!canSave}
          >
            Save & update
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangeRoleModal;
